import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import AppShell from '../../layout/AppShell';
import api from '../../services/api';

/**
 * Return a normalized seller status.
 */
const getSellerStatus = (seller) => {
  if (!seller) return 'pending';

  if (seller.status) {
    return String(seller.status).toLowerCase();
  }

  if (seller.approval_status) {
    return String(
      seller.approval_status
    ).toLowerCase();
  }

  if (
    seller.is_approved === true ||
    seller.is_approved === 1
  ) {
    return 'approved';
  }

  if (
    seller.approved === true ||
    seller.approved === 1
  ) {
    return 'approved';
  }

  if (
    seller.is_active === true ||
    seller.is_active === 1
  ) {
    return 'approved';
  }

  if (
    seller.is_approved === false ||
    seller.is_approved === 0
  ) {
    return 'pending';
  }

  if (
    seller.approved === false ||
    seller.approved === 0
  ) {
    return 'pending';
  }

  return 'pending';
};

/**
 * Render status chip.
 */
const getStatusChip = (seller) => {
  const value = getSellerStatus(seller);

  if (
    value === 'approved' ||
    value === 'active'
  ) {
    return (
      <Chip
        size="small"
        label="Approved"
        color="success"
      />
    );
  }

  if (value === 'blocked') {
    return (
      <Chip
        size="small"
        label="Blocked"
        color="error"
      />
    );
  }

  if (value === 'rejected') {
    return (
      <Chip
        size="small"
        label="Rejected"
        color="error"
      />
    );
  }

  return (
    <Chip
      size="small"
      label="Pending"
      color="warning"
    />
  );
};

function AdminSellers() {
  const [sellers, setSellers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search input shown to the user.
  const [searchInput, setSearchInput] =
    useState('');

  // Debounced value sent to backend.
  const [search, setSearch] = useState('');

  // MUI TablePagination uses zero-based pages.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] =
    useState(20);

  const [totalSellers, setTotalSellers] =
    useState(0);

  // Existing Block / Unblock states
  const [
    actionLoadingId,
    setActionLoadingId,
  ] = useState(null);

  const [confirmOpen, setConfirmOpen] =
    useState(false);

  const [
    selectedSeller,
    setSelectedSeller,
  ] = useState(null);

  // ==========================================
  // EDIT SELLER STATES
  // ==========================================

  const [editOpen, setEditOpen] =
    useState(false);

  const [editSeller, setEditSeller] =
    useState(null);

  const [editLoading, setEditLoading] =
    useState(false);

  const [editError, setEditError] =
    useState('');

  // ==========================================
  // FETCH SELLERS
  // ==========================================

  const fetchSellers = useCallback(
    async () => {
      try {
        setLoading(true);
        setError('');

        const res = await api.get(
          '/admin/sellers',
          {
            params: {
              // Backend uses one-based pages.
              page: page + 1,
              pageSize,
              search: search.trim(),
            },
          }
        );

        const data = res.data?.data || {};

        const list =
          data.items ||
          res.data?.sellers ||
          (Array.isArray(data) ? data : []);

        setSellers(
          Array.isArray(list) ? list : []
        );

        // Paginated API response
        if (
          typeof data.total !== 'undefined'
        ) {
          setTotalSellers(
            Number(data.total) || 0
          );
        } else {
          // Compatibility with older response.
          setTotalSellers(
            Array.isArray(list)
              ? list.length
              : 0
          );
        }
      } catch (err) {
        if (err.response?.status === 401) {
          setError(
            'Your admin login session expired. Please login again.'
          );
        } else {
          setError(
            err.response?.data?.message ||
              'Failed to load sellers.'
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, search]
  );

  /**
   * Reload whenever pagination/search changes.
   */
  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  /**
   * Debounce global database search.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // ==========================================
  // PAGINATION
  // ==========================================

  const handleRefresh = () => {
    fetchSellers();
  };

  const handlePageChange = (
    event,
    newPage
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event
  ) => {
    const newPageSize = Number(
      event.target.value
    );

    setPageSize(newPageSize);
    setPage(0);
  };

  // ==========================================
  // EDIT SELLER
  // ==========================================

  const handleOpenEditSeller = (
    seller
  ) => {
    setEditSeller(seller);
    setEditError('');
    setEditOpen(true);
  };

  const handleCloseEditSeller = () => {
    if (editLoading) return;

    setEditOpen(false);
    setEditSeller(null);
    setEditError('');
  };

  const handleSaveSeller = async (
    formData
  ) => {
    if (!editSeller) return;

    try {
      setEditLoading(true);
      setEditError('');
      setError('');

      await api.patch(
        `/admin/sellers/${editSeller.id}`,
        formData
      );

      setEditOpen(false);
      setEditSeller(null);

      // Refresh current search/page.
      await fetchSellers();
    } catch (err) {
      setEditError(
        err.response?.data?.message ||
          'Failed to update seller details.'
      );
    } finally {
      setEditLoading(false);
    }
  };

  // ==========================================
  // BLOCK / UNBLOCK SELLER
  // ==========================================

  const handleToggleBlockSeller = (
    seller
  ) => {
    setSelectedSeller(seller);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (actionLoadingId) return;

    setConfirmOpen(false);
    setSelectedSeller(null);
  };

  const handleConfirmSellerAction =
    async () => {
      if (!selectedSeller) return;

      const status =
        getSellerStatus(selectedSeller);

      const isBlocked =
        status === 'blocked';

      try {
        setActionLoadingId(
          selectedSeller.id
        );

        setError('');

        if (isBlocked) {
          await api.patch(
            `/admin/sellers/${selectedSeller.id}/unblock`
          );
        } else {
          await api.patch(
            `/admin/sellers/${selectedSeller.id}/block`
          );
        }

        setConfirmOpen(false);
        setSelectedSeller(null);

        // Reload current page/search.
        await fetchSellers();
      } catch (err) {
        setError(
          err.response?.data?.message ||
            `Failed to ${
              isBlocked
                ? 'unblock'
                : 'block'
            } seller.`
        );
      } finally {
        setActionLoadingId(null);
      }
    };

  /**
   * These counts represent only the
   * currently visible page.
   *
   * Total Sellers is database-wide.
   */
  const pageSummary = useMemo(() => {
    const approved = sellers.filter(
      (seller) =>
        ['approved', 'active'].includes(
          getSellerStatus(seller)
        )
    ).length;

    const pending = sellers.filter(
      (seller) =>
        [
          'pending',
          'waiting',
          'inactive',
        ].includes(
          getSellerStatus(seller)
        )
    ).length;

    const blocked = sellers.filter(
      (seller) =>
        getSellerStatus(seller) ===
        'blocked'
    ).length;

    return {
      approved,
      pending,
      blocked,
    };
  }, [sellers]);

  const showingFrom =
    totalSellers === 0
      ? 0
      : page * pageSize + 1;

  const showingTo =
    totalSellers === 0
      ? 0
      : Math.min(
          page * pageSize +
            sellers.length,
          totalSellers
        );

  return (    <AppShell
      role="admin"
      title="Sellers"
    >
      <Box sx={{ pb: 4 }}>
        {/* =====================================
            HEADER
        ====================================== */}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 2.5,
              md: 3,
            },
            mb: 3,
            borderRadius: 4,
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
            background:
              'linear-gradient(135deg, #0F172A 0%, #1E40AF 55%, #2563EB 100%)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 260,
              height: 260,
              borderRadius: '50%',
              bgcolor:
                'rgba(255,255,255,0.08)',
              right: -90,
              top: -110,
            }}
          />

          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
            spacing={2}
            sx={{
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: 4,
                  bgcolor:
                    'rgba(255,255,255,0.16)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent:
                    'center',
                }}
              >
                <StorefrontOutlinedIcon
                  sx={{
                    fontSize: 36,
                  }}
                />
              </Box>

              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 0.8 }}
                >
                  <Chip
                    size="small"
                    label="Admin"
                    sx={{
                      bgcolor:
                        'rgba(255,255,255,0.18)',
                      color: '#fff',
                      fontWeight: 800,
                    }}
                  />

                  <Chip
                    size="small"
                    label="Seller Management"
                    sx={{
                      bgcolor:
                        'rgba(34,197,94,0.22)',
                      color: '#DCFCE7',
                      fontWeight: 800,
                    }}
                  />
                </Stack>

                <Typography
                  variant="h4"
                  fontWeight={900}
                >
                  Sellers
                </Typography>

                <Typography
                  sx={{
                    mt: 0.6,
                    color:
                      'rgba(255,255,255,0.78)',
                  }}
                >
                  View and edit registered
                  sellers, account status, and
                  block or unblock seller
                  access.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              startIcon={
                <RefreshOutlinedIcon />
              }
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                bgcolor: '#fff',
                color: '#1E40AF',
                borderRadius: 3,
                fontWeight: 900,
                px: 2.5,
                boxShadow: 'none',

                '&:hover': {
                  bgcolor: '#F8FAFC',
                  boxShadow: 'none',
                },
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Paper>

        {/* =====================================
            SUMMARY
        ====================================== */}

        <Grid
          container
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <SummaryCard
              title="Total Sellers"
              value={totalSellers}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <SummaryCard
              title="Approved (This Page)"
              value={pageSummary.approved}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <SummaryCard
              title="Pending (This Page)"
              value={pageSummary.pending}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <SummaryCard
              title="Blocked (This Page)"
              value={pageSummary.blocked}
            />
          </Grid>
        </Grid>

        {error ? (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 3,
            }}
          >
            {error}
          </Alert>
        ) : null}

        {/* =====================================
            SELLERS
        ====================================== */}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 2.5,
              md: 3,
            },
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              md: 'center',
            }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={900}
              >
                Registered Sellers
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                {totalSellers === 0
                  ? 'Showing 0 sellers.'
                  : `Showing ${showingFrom}-${showingTo} of ${totalSellers} sellers.`}
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="Search all sellers..."
              value={searchInput}
              onChange={(e) =>
                setSearchInput(
                  e.target.value
                )
              }
              sx={{
                width: {
                  xs: '100%',
                  md: 340,
                },

                '& .MuiOutlinedInput-root':
                  {
                    borderRadius: 3,
                    bgcolor: '#fff',
                  },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {loading ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 6 }}
            >
              <CircularProgress />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2 }}
              >
                Loading sellers...
              </Typography>
            </Stack>
          ) : sellers.length === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 6 }}
            >
              <StorefrontOutlinedIcon
                sx={{
                  fontSize: 48,
                  color: '#94A3B8',
                }}
              />

              <Typography
                variant="h6"
                fontWeight={900}
                sx={{ mt: 1 }}
              >
                No sellers found
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                {search
                  ? 'No sellers match your search.'
                  : 'Sellers will appear here after registration.'}
              </Typography>
            </Stack>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        ID
                      </TableCell>

                      <TableCell>
                        Seller / Business
                      </TableCell>

                      <TableCell>
                        Contact
                      </TableCell>

                      <TableCell>
                        BR / NIC
                      </TableCell>

                      <TableCell>
                        Address
                      </TableCell>

                      <TableCell>
                        Status
                      </TableCell>

                      <TableCell>
                        Registered
                      </TableCell>

                      <TableCell align="right">
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sellers.map(
                      (seller) => {
                        const businessRegNo =
                          seller.business_reg_no ||
                          seller.businessRegNo ||
                          seller.business_registration_no ||
                          '-';

                        const telephone =
                          seller.telephone ||
                          seller.phone ||
                          seller.mobile ||
                          '-';

                        const sellerStatus =
                          getSellerStatus(
                            seller
                          );

                        const isBlocked =
                          sellerStatus ===
                          'blocked';

                        return (
                          <TableRow
                            key={
                              seller.id
                            }
                            hover
                          >
                            <TableCell>
                              <Typography
                                fontWeight={
                                  800
                                }
                              >
                                #
                                {
                                  seller.id
                                }
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Stack
                                spacing={
                                  0.4
                                }
                              >
                                <Typography
                                  fontWeight={
                                    900
                                  }
                                >
                                  {seller.name ||
                                    seller.business_name ||
                                    'Unnamed Seller'}
                                </Typography>

                                <Stack
                                  direction="row"
                                  spacing={
                                    0.6
                                  }
                                  alignItems="center"
                                >
                                  <EmailOutlinedIcon
                                    sx={{
                                      fontSize: 15,
                                      color:
                                        '#64748B',
                                    }}
                                  />

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {seller.email ||
                                      '-'}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={
                                  0.6
                                }
                                alignItems="center"
                              >
                                <PhoneOutlinedIcon
                                  sx={{
                                    fontSize: 16,
                                    color:
                                      '#64748B',
                                  }}
                                />

                                <Typography variant="body2">
                                  {
                                    telephone
                                  }
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Stack
                                spacing={
                                  0.4
                                }
                              >
                                <Stack
                                  direction="row"
                                  spacing={
                                    0.6
                                  }
                                  alignItems="center"
                                >
                                  <BusinessOutlinedIcon
                                    sx={{
                                      fontSize: 16,
                                      color:
                                        '#64748B',
                                    }}
                                  />

                                  <Typography variant="body2">
                                    {
                                      businessRegNo
                                    }
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="row"
                                  spacing={
                                    0.6
                                  }
                                  alignItems="center"
                                >
                                  <BadgeOutlinedIcon
                                    sx={{
                                      fontSize: 16,
                                      color:
                                        '#64748B',
                                    }}
                                  />

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {seller.nic ||
                                      '-'}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </TableCell>

                            <TableCell
                              sx={{
                                maxWidth: 260,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow:
                                    'hidden',
                                  textOverflow:
                                    'ellipsis',
                                  display:
                                    '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient:
                                    'vertical',
                                }}
                              >
                                {seller.address ||
                                  '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {getStatusChip(
                                seller
                              )}
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {seller.created_at
                                  ? new Date(
                                      seller.created_at
                                    ).toLocaleDateString()
                                  : '-'}
                              </Typography>
                            </TableCell>

                            {/* =====================
                                EDIT + BLOCK ACTIONS
                            ====================== */}

                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    <EditOutlinedIcon />
                                  }
                                  disabled={
                                    actionLoadingId ===
                                      seller.id ||
                                    editLoading
                                  }
                                  onClick={() =>
                                    handleOpenEditSeller(
                                      seller
                                    )
                                  }
                                  sx={{
                                    borderRadius: 999,
                                    textTransform:
                                      'none',
                                    fontWeight: 900,
                                    minWidth: 82,
                                  }}
                                >
                                  Edit
                                </Button>

                                <Button
                                  size="small"
                                  variant={
                                    isBlocked
                                      ? 'contained'
                                      : 'outlined'
                                  }
                                  color={
                                    isBlocked
                                      ? 'success'
                                      : 'error'
                                  }
                                  disabled={
                                    actionLoadingId ===
                                      seller.id ||
                                    editLoading
                                  }
                                  onClick={() =>
                                    handleToggleBlockSeller(
                                      seller
                                    )
                                  }
                                  sx={{
                                    borderRadius: 999,
                                    textTransform:
                                      'none',
                                    fontWeight: 900,
                                    minWidth: 96,
                                  }}
                                >
                                  {actionLoadingId ===
                                  seller.id
                                    ? 'Saving...'
                                    : isBlocked
                                      ? 'Unblock'
                                      : 'Block'}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalSellers}
                page={page}
                onPageChange={
                  handlePageChange
                }
                rowsPerPage={pageSize}
                onRowsPerPageChange={
                  handleRowsPerPageChange
                }
                rowsPerPageOptions={[
                  20,
                  50,
                  100,
                ]}
                labelRowsPerPage="Sellers per page:"
                showFirstButton
                showLastButton
              />
            </>
          )}
        </Paper>

        {/* =====================================
            EDIT SELLER DIALOG
        ====================================== */}

        <SellerEditDialog
          open={editOpen}
          seller={editSeller}
          loading={editLoading}
          error={editError}
          onClose={
            handleCloseEditSeller
          }
          onSave={handleSaveSeller}
        />

        {/* Existing Block / Unblock dialog */}

        <SellerActionDialog
          open={confirmOpen}
          seller={selectedSeller}
          loading={Boolean(
            actionLoadingId
          )}
          getSellerStatus={
            getSellerStatus
          }
          onClose={
            handleCloseConfirm
          }
          onConfirm={
            handleConfirmSellerAction
          }
        />
      </Box>
    </AppShell>
  );
}// ======================================================
// EDIT SELLER DIALOG
// ======================================================

function SellerEditDialog({
  open,
  seller,
  loading,
  error,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState({
    name: '',
    businessRegNo: '',
    email: '',
    telephone: '',
    nic: '',
    address: '',
  });

  useEffect(() => {
    if (!open || !seller) {
      return;
    }

    setForm({
      name:
        seller.name ||
        seller.business_name ||
        '',

      businessRegNo:
        seller.business_reg_no ||
        seller.businessRegNo ||
        seller.business_registration_no ||
        '',

      email:
        seller.email || '',

      telephone:
        seller.telephone ||
        seller.phone ||
        seller.mobile ||
        '',

      nic:
        seller.nic || '',

      address:
        seller.address || '',
    });
  }, [open, seller]);

  const handleChange =
    (field) => (event) => {
      setForm((previous) => ({
        ...previous,
        [field]:
          event.target.value,
      }));
    };

  const handleSubmit = (
    event
  ) => {
    event.preventDefault();

    const payload = {
      name:
        form.name.trim(),

      businessRegNo:
        form.businessRegNo.trim(),

      email:
        form.email.trim(),

      telephone:
        form.telephone.trim(),

      nic:
        form.nic.trim(),

      address:
        form.address.trim(),
    };

    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              color: '#fff',
              background:
                'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 3,
                    bgcolor:
                      'rgba(255,255,255,0.16)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      'center',
                  }}
                >
                  <EditOutlinedIcon />
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={900}
                  >
                    Edit Seller
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        'rgba(255,255,255,0.8)',
                    }}
                  >
                    Seller #
                    {seller?.id ||
                      '-'}
                  </Typography>
                </Box>
              </Stack>

              <IconButton
                onClick={onClose}
                disabled={loading}
                sx={{
                  color: '#fff',
                  width: 36,
                  height: 36,
                  bgcolor:
                    'rgba(255,255,255,0.12)',

                  '&:hover': {
                    bgcolor:
                      'rgba(255,255,255,0.2)',
                  },
                }}
              >
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            py: 3,
          }}
        >
          {error ? (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          ) : null}

          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
            >
              <TextField
                label="Seller / Business Name"
                value={form.name}
                onChange={
                  handleChange(
                    'name'
                  )
                }
                required
                fullWidth
                disabled={
                  loading
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                label="Business Registration No."
                value={
                  form.businessRegNo
                }
                onChange={
                  handleChange(
                    'businessRegNo'
                  )
                }
                required
                fullWidth
                disabled={
                  loading
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                label="NIC"
                value={
                  form.nic
                }
                onChange={
                  handleChange(
                    'nic'
                  )
                }
                required
                fullWidth
                disabled={
                  loading
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                label="Email"
                type="email"
                value={
                  form.email
                }
                onChange={
                  handleChange(
                    'email'
                  )
                }
                required
                fullWidth
                disabled={
                  loading
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                label="Telephone"
                value={
                  form.telephone
                }
                onChange={
                  handleChange(
                    'telephone'
                  )
                }
                required
                fullWidth
                disabled={
                  loading
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
            >
              <TextField
                label="Address"
                value={
                  form.address
                }
                onChange={
                  handleChange(
                    'address'
                  )
                }
                required
                fullWidth
                multiline
                minRows={3}
                disabled={
                  loading
                }
              />
            </Grid>
          </Grid>

          <Alert
            severity="info"
            sx={{
              mt: 2.5,
              borderRadius: 2,
            }}
          >
            Editing these
            details will not
            change the seller's
            approval,
            blocked/active
            status, or
            password.
          </Alert>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              borderRadius: 999,
              textTransform:
                'none',
              fontWeight: 900,
              px: 2.5,
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={loading}
            variant="contained"
            sx={{
              borderRadius: 999,
              textTransform:
                'none',
              fontWeight: 900,
              px: 3,
              boxShadow: 'none',
            }}
          >
            {loading
              ? 'Saving...'
              : 'Save Changes'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

// ======================================================
// EXISTING BLOCK / UNBLOCK DIALOG
// ======================================================

function SellerActionDialog({
  open,
  seller,
  loading,
  getSellerStatus,
  onClose,
  onConfirm,
}) {
  const status =
    getSellerStatus(seller);

  const isBlocked =
    status === 'blocked';

  const sellerName =
    seller?.name ||
    seller?.business_name ||
    'this seller';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{ p: 0 }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            color: '#fff',

            background:
              isBlocked
                ? 'linear-gradient(135deg, #15803D 0%, #22C55E 100%)'
                : 'linear-gradient(135deg, #991B1B 0%, #EF4444 100%)',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 3,
                  bgcolor:
                    'rgba(255,255,255,0.18)',
                  display: 'flex',
                  alignItems:
                    'center',
                  justifyContent:
                    'center',
                }}
              >
                {isBlocked ? (
                  <CheckCircleOutlineOutlinedIcon
                    sx={{
                      fontSize: 28,
                    }}
                  />
                ) : (
                  <BlockOutlinedIcon
                    sx={{
                      fontSize: 28,
                    }}
                  />
                )}
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  fontWeight={900}
                >
                  {isBlocked
                    ? 'Unblock Seller?'
                    : 'Block Seller?'}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color:
                      'rgba(255,255,255,0.82)',
                  }}
                >
                  Admin
                  confirmation
                  required
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={onClose}
              disabled={loading}
              sx={{
                color: '#fff',
                width: 36,
                height: 36,
                bgcolor:
                  'rgba(255,255,255,0.12)',

                '&:hover': {
                  bgcolor:
                    'rgba(255,255,255,0.2)',
                },
              }}
            >
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 3,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={900}
          sx={{ mb: 1 }}
        >
          {isBlocked
            ? `Restore access for ${sellerName}?`
            : `Restrict access for ${sellerName}?`}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.8,
          }}
        >
          {isBlocked
            ? `${sellerName} will be able to login again and continue using the seller dashboard.`
            : `${sellerName} will be blocked from logging in and will not be able to access the seller dashboard until you unblock the account.`}
        </Typography>

        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: 3,

            bgcolor:
              isBlocked
                ? '#F0FDF4'
                : '#FEF2F2',

            border:
              '1px solid',

            borderColor:
              isBlocked
                ? '#BBF7D0'
                : '#FECACA',
          }}
        >
          <Typography
            variant="body2"
            fontWeight={800}
            sx={{
              color:
                isBlocked
                  ? '#15803D'
                  : '#B91C1C',
            }}
          >
            Seller:{' '}
            {sellerName}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            Email:{' '}
            {seller?.email ||
              '-'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 999,
            textTransform:
              'none',
            fontWeight: 900,
            px: 2.5,
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={
            isBlocked
              ? 'success'
              : 'error'
          }
          sx={{
            borderRadius: 999,
            textTransform:
              'none',
            fontWeight: 900,
            px: 3,
            boxShadow: 'none',
          }}
        >
          {loading
            ? 'Processing...'
            : isBlocked
              ? 'Yes, Unblock Seller'
              : 'Yes, Block Seller'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ======================================================
// SUMMARY CARD
// ======================================================

function SummaryCard({
  title,
  value,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        borderRadius: 4,
        border: '1px solid',
        borderColor:
          'divider',
        bgcolor: '#fff',
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={700}
      >
        {title}
      </Typography>

      <Typography
        variant="h4"
        fontWeight={900}
        sx={{ mt: 0.6 }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

export default AdminSellers;